/**
 * Coverage Area module theme — aliases app brand (Patient indigo SoT).
 *
 * Used by Coverage Area list page + CoverageAreaModal (Profile / Employees).
 * Color changes still flow from patientTheme via brandTheme.
 */
import { BRAND_THEME, BRAND_THEME_HEX } from "../ui/brandTheme";

export const COVERAGE_THEME = BRAND_THEME;
export const COVERAGE_THEME_HEX = BRAND_THEME_HEX;

/** Shared role chip — one brand tone (not per-role rainbow). */
export const COVERAGE_ROLE_CHIP =
  "bg-indigo-50 text-indigo-700 border border-indigo-100";

export const COVERAGE_BTN = {
  primary: `inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-semibold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${BRAND_THEME.button.primary}`,
  outline: `inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md border transition-colors disabled:opacity-50 ${BRAND_THEME.button.outline}`,
  link: `text-[10px] font-semibold ${BRAND_THEME.button.link}`,
  /** List row Edit trigger — matches Patient module soft button. */
  edit: `inline-flex items-center justify-center gap-1 h-7 px-2.5 text-xs font-medium rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${BRAND_THEME.button.soft}`,
  menuPanel:
    "absolute right-0 top-full z-30 mt-1 min-w-[12.5rem] rounded-lg border border-indigo-200 bg-white shadow-lg py-1 overflow-hidden",
  menuItem:
    "w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-indigo-50 transition-colors text-left",
  menuItemIcon: "text-indigo-600 shrink-0",
  modalPrimary: `inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-semibold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${BRAND_THEME.button.primary}`,
  modalCancel:
    "inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50",
  chipRemove:
    "inline-flex items-center justify-center rounded text-slate-400 hover:text-slate-700 transition-colors",
};

export const COVERAGE_FIELD = {
  search: `w-full h-8 pl-9 pr-3 text-sm bg-white border border-indigo-200 rounded-md text-slate-800 placeholder:text-slate-400 outline-none transition-colors ${BRAND_THEME.fieldFocus}`,
  searchSm: `w-full h-8 pl-8 pr-3 text-xs bg-white border border-indigo-200 rounded-md text-slate-700 placeholder:text-slate-400 outline-none transition-colors ${BRAND_THEME.fieldFocus}`,
  /** Section labels (Cities / Roles) — black, not indigo. */
  sectionLabel:
    "text-xs font-semibold text-slate-900 uppercase tracking-wider mb-2",
  sectionLabelCompact:
    "text-[10px] font-semibold text-slate-900 uppercase tracking-wide truncate",
};

export const COVERAGE_CHECKBOX = {
  wrapSelected: "bg-indigo-50/60",
  wrapIdle: "hover:bg-slate-50",
  /** Native square checkbox (Patient pattern) — `accent-*` keeps it indigo. */
  input:
    "h-3 w-3 shrink-0 rounded border-slate-300 accent-indigo-600 text-indigo-600 focus:ring-indigo-200 cursor-pointer",
  labelSelected: "text-xs truncate text-indigo-800 font-medium",
  labelIdle: "text-xs truncate text-slate-700",
  listBox:
    "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5 max-h-40 overflow-y-auto py-1 pr-1 border border-indigo-200 rounded-md bg-white",
  /** Compact 4-column filter panel scroll area */
  listBoxCompact:
    "grid grid-cols-2 gap-x-2 gap-y-0.5 overflow-y-auto max-h-[9.5rem] py-0.5 pr-0.5",
  filterColumn:
    "flex flex-col min-h-0 rounded-md border border-indigo-100 bg-white overflow-hidden",
  filterColumnHead:
    "flex items-center justify-between gap-1 px-2 py-1 border-b border-indigo-50 bg-indigo-50/40 shrink-0",
  filterColumnBody: "px-1.5 py-1 min-h-0 flex-1 overflow-hidden",
};

export const COVERAGE_MODAL = {
  backdrop:
    "fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40",
  panel:
    "w-full max-w-2xl rounded-xl border border-indigo-200 bg-white shadow-xl overflow-hidden max-h-[90vh] flex flex-col",
  head: `flex items-start justify-between gap-3 px-4 py-3 border-b shrink-0 ${BRAND_THEME.modalHead}`,
  title: `text-sm font-semibold uppercase tracking-wide ${BRAND_THEME.modalTitle}`,
  subtitle: "mt-0.5 text-[11px] text-slate-600 normal-case tracking-normal font-normal",
  body: "p-4 overflow-y-auto flex-1 min-h-0",
  footer:
    "flex items-center justify-end gap-2 px-4 py-3 border-t border-indigo-100 bg-indigo-50/40 shrink-0",
  close:
    "inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-indigo-50 hover:text-indigo-700 transition-colors shrink-0 disabled:opacity-50",
};

export const COVERAGE_COL = {
  srNo: "w-[4%] min-w-[2.75rem] text-center align-middle",
  name: "w-[13%] min-w-0 pl-3 pr-2 align-middle",
  email: "w-[17%] min-w-0 px-2 align-middle",
  role: "w-[8%] whitespace-nowrap px-2 align-middle",
  areas: "w-[21%] min-w-0 pl-2 pr-2 align-middle",
  availability: "w-[21%] min-w-0 px-2 align-middle",
  actions: "w-[11%] min-w-[5.75rem] whitespace-nowrap pl-2 pr-3 text-right align-middle",
};
